/* eslint-disable */
import LazySizes from 'lazysizes';
import Unveilhooks from 'lazysizes/plugins/unveilhooks/ls.unveilhooks';
/* eslint-enable */

import { onDocumentReady } from './utils';

// Shared animation libs
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Shared Libs
import * as algoliSearchLite from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import {
  configure,
  searchBox,
  clearRefinements,
  hits,
  refinementList,
  panel,
  stats,
  menu,
  menuSelect,
  toggleRefinement
} from 'instantsearch.js/es/widgets';
import { history } from 'instantsearch.js/es/lib/routers';
import { connectHits, connectRange, connectPagination, connectInfiniteHits } from 'instantsearch.js/es/connectors';
import { createInsightsMiddleware } from 'instantsearch.js/es/middlewares';

gsap.registerPlugin(ScrollTrigger);

window.pratt.PrattSharedLibs = {
  gsap,
  ScrollTrigger,
  algoliSearchLite,
  instantsearch: {
    core: instantsearch,
    widgets: {
      configure,
      searchBox,
      clearRefinements,
      hits,
      refinementList,
      panel,
      stats,
      menu,
      menuSelect,
      toggleRefinement
    },
    routers: {
      history
    },
    connectors: {
      connectHits,
      connectRange,
      connectPagination,
      connectInfiniteHits
    },
    middlewares: {
      createInsightsMiddleware
    }
  }
}

// Components
import Menu from '@pratt/js/components/menu';
import Links from '@pratt/js/components/links';

onDocumentReady(() => {
  // Update HTML Classlist.
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  // Detect prefers-reduced-motion and persist it in a cookie so the
  // server can read it on subsequent requests.
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const updateReducedMotion = () => {
    const reducedMotion = prefersReducedMotion.matches ? 'reduce' : 'no-preference';
    window.pratt.prefers_reduced_motion = prefersReducedMotion.matches;
    const cookieMatch = document.cookie.match(/(?:^|; )prefers_reduced_motion=([^;]+)/);
    const currentCookie = cookieMatch ? cookieMatch[1] : null;
    if (currentCookie !== reducedMotion) {
      document.cookie = `prefers_reduced_motion=${reducedMotion}; path=/; max-age=31536000; SameSite=Lax`;
    }
  };
  updateReducedMotion();
  prefersReducedMotion.addEventListener('change', updateReducedMotion);

  new Links();

  const MENU_CLASS = '.js--menu';
  if (document.querySelector(MENU_CLASS)) {
    new Menu(MENU_CLASS);
  }

  if (process.env.NODE_ENV === 'development') {
    import(/* webpackChunkName: "toggle-grid" */ '@pratt/js/components/toggle-grid').then(
      module => {
        const ToggleGrid = module.default;
        new ToggleGrid();
      },
    );
  }

  if (document.querySelector('.js-modal-gallery')) {
    import(/* webpackChunkName: "modal-gallery" */ '@pratt/js/components/modal-gallery').then(
      module => {
        const ModalGallery = module.default;
        new ModalGallery();
      },
    );
  }

  if (document.querySelector('.js-hang-punc')) {
    import(/* webpackChunkName: "hang-punctuation" */ '@pratt/js/components/hang-punctuation').then(
      module => {
        const HangPunctuation = module.default;
        new HangPunctuation();
      },
    );
  }

  if (document.querySelector('.js-work-block')) {
    import(/* webpackChunkName: "work-block" */ '@pratt/js/components/work-block').then(module => {
      const WorkBlock = module.default;
      new WorkBlock();
    });
  }

  const globalSearchRoot = document.querySelector('.js-global-search');
  if (globalSearchRoot) {
    import(/* webpackChunkName: "search" */ '@pratt/js/components/algolia/global-search').then(
      module => {
        const GlobalSearch = module.default;
        new GlobalSearch(globalSearchRoot);
      },
    );
  }

  const errorSearchRoot = document.querySelector('.js-error-search');
  if (errorSearchRoot) {
    import(
      /* webpackChunkName: "inline-search" */ '@pratt/js/components/algolia/inline-search'
    ).then(module => {
      const ErrorSearch = module.default;
      new ErrorSearch('.js-error-search', '.js-error-search-input', '.js-error-search-hits');
    });
  }

  const articleSearchRoot = document.querySelector('.js-article-search');
  if (articleSearchRoot) {
    import(
      /* webpackChunkName: "article-search" */ '@pratt/js/components/algolia/article-search'
    ).then(module => {
      const ArticleSearch = module.default;
      new ArticleSearch(articleSearchRoot);
    });
  }

  const dailyHubSearchRoot = document.querySelector('.js-daily-hub-search');
  if (dailyHubSearchRoot) {
    import(/* webpackChunkName: "daily-hub-search" */ './components/algolia/daily-hub-search').then(
      module => {
        const DailyHubSearch = module.default;
        new DailyHubSearch(dailyHubSearchRoot);
      },
    );
  }

  const parallaxRoot = document.querySelector('.js-parallax');
  if (parallaxRoot) {
    import(/* webpackChunkName: "parallax-image" */ '@pratt/js/components/parallax-image')
      .then(module => {
        const ParallaxImage = module.default;
        new ParallaxImage();
      })
      .catch(error => {
        console.error('Failed to load parallax-image:', error);
      });
  }

  const sliderControls = document.querySelectorAll('.js-slider-container');
  if (sliderControls) {
    import(/* webpackChunkName: "slider-controls" */ '@pratt/js/components/slider-controls').then(
      module => {
        const SliderControls = module.default;
        sliderControls.forEach(sliderContainer => new SliderControls(sliderContainer));
      },
    );
  }

  if (document.querySelector('.js-share-trigger')) {
    import(/* webpackChunkName: "share-overlay" */ '@pratt/js/components/share-overlay').then(
      module => {
        const ShareOverlay = module.default;
        new ShareOverlay();
      },
    );
  }

  if (document.querySelector('.js-create-virtual-calendar')) {
    import(/* webpackChunkName: "virtual-calendar" */ '@pratt/js/components/virtual-calendar').then(
      module => {
        const VirtualCalendar = module.default;
        new VirtualCalendar();
      },
    );
  }

  if (document.querySelector('.js-tease-modal')) {
    import(/* webpackChunkName: "tease-modal" */ '@pratt/js/components/tease-modal').then(
      module => {
        const TeaseModal = module.default;
        const triggers = document.querySelectorAll('.js-tease-modal-trigger');
        triggers.forEach(trigger => new TeaseModal(trigger));
      },
    );
  }

  // .js-accordion-block gates the theme's own accordion blocks; the PCT
  // show/hide group block reuses the same markup contract (accordion +
  // .js-accordion-trigger) without that class, so its wrapper hook
  // [data-pct-show-hide] also needs to load the accordion component.
  if (document.querySelector('.js-accordion-block, [data-pct-show-hide]')) {
    import(/* webpackChunkName: "accordion" */ '@pratt/js/components/accordion').then(
      module => {
        const Accordion = module.default;
        const triggers = document.querySelectorAll('.js-accordion-trigger');
        triggers.forEach(trigger => new Accordion(trigger));
      },
    );
  }

  if (document.querySelector('.js-mobile-only-accordion-block')) {
    import(
      /* webpackChunkName: "mobile-accordion" */ '@pratt/js/components/mobile-only-accordion'
    ).then(module => {
      const MobileOnlyAccordion = module.default;
      const triggers = document.querySelectorAll('.js-mobile-only-accordion-trigger');
      triggers.forEach(trigger => new MobileOnlyAccordion(trigger));
    });
  }

  const tagContainers = document.querySelectorAll('.js-tags');
  if (tagContainers) {
    import(/* webpackChunkName: "tag-shape" */ '@pratt/js/components/tag').then(module => {
      const Tag = module.default;
      tagContainers.forEach(tagContainer => {
        const options = {
          shape: tagContainer.dataset['shape'], // 'ellipse' or 'graph'
          duration: 15000, // for one full rotation, in ms
          reverse: false, // false = clockwise, true = counter-clockwise
        };
        new Tag(tagContainer, options);
      });
    });
  }

  const croppedImageSelector =
    '.ups-block__asym-grid.is-cropped .article-asset-mod.has-caption .lazy-img__mod, ' +
    '.ups-block__row.is-cropped .article-asset-mod.has-caption .lazy-img__mod';
  if (document.querySelector(croppedImageSelector)) {
    import(/* webpackChunkName: "cropped-caption-resizer" */ '@pratt/js/components/cropped-caption-resizer').then(
      module => {
        const CroppedCaptionResizer = module.default;

        // Find all relevant images and create an instance for each
        const containersToAdjust = document.querySelectorAll(croppedImageSelector);
        containersToAdjust.forEach(container => new CroppedCaptionResizer(container));
      },
    );
  }

  // This is an example of how to do code splitting. The JS in this
  // referenced file will only be loaded on that page. Good for
  // when you have a large amount of JS only needed in one place
  //
  //  if (document.querySelector('#js-process')) {
  //    import(/* webpackChunkName: "process" */ './pages/process')
  //      .then(module => {
  //        const Process = module.default;
  //        this.process = new Process();
  //      });
  //  }
});

// Enables hot module replacement with Webpack
// https://webpack.js.org/guides/hot-module-replacement/
if (module.hot) {
  module.hot.accept();
}
